import React, { Dispatch, SetStateAction, createContext, useEffect, useState } from "react";
import { useContext } from "react";
import {
  CpfDetailConstant,
  CpfDetailInput,
  CpfDetailState,
  DerivedValue,
  ExtraFeeConstant,
  ExtraFeeInput,
  ExtraFeeState,
  FeeConstant,
  FeeInput,
  FeeState,
  FinancialPlanConstant,
  FinancialPlanInput,
  HpsFeeConstant,
  HpsFeeInput,
  HpsFeeState,
  LoanDetailConstant,
  LoanDetailInput,
  LoanDetailState,
  WithAdditionalAmount,
} from "../pages/application-coo-stage-d/types";
import { DUMMY_RESPONSE } from "./dummy";
import { updateArrayState, updateSingleState } from "../utils/financial-plan";

export interface FinancialPlanContenxtInterface {
  isAdjustFPVisible: boolean;
  constant: FinancialPlanConstant;
  hdb_input: FinancialPlanInput;
  others_input: FinancialPlanInput;
  indexOfCurrentCpfDetail: number;
  cpfDetails: CpfDetailState[];
  loanDetail: LoanDetailState;
  stampFee: FeeState | undefined;
  conveyancingFee: FeeState | undefined;
  registrationFee: FeeState | undefined;
  hpsFees: HpsFeeState[];
  extraFees: ExtraFeeState[];
  derivedValue: DerivedValue;
  toggleAdjustFPVisibility?: () => void;
  setIndexOfCurrentCpfDetail: Dispatch<SetStateAction<number>>;
  setCpfDetails: Dispatch<SetStateAction<CpfDetailState[]>>;
  setLoanDetail: Dispatch<SetStateAction<LoanDetailState>>;
  setStampFee: Dispatch<SetStateAction<FeeState | undefined>>;
  setConveyancingFee: Dispatch<SetStateAction<FeeState | undefined>>;
  setRegistrationFee: Dispatch<SetStateAction<FeeState | undefined>>;
  setHpsFees: Dispatch<SetStateAction<HpsFeeState[]>>;
  setExtraFees: Dispatch<SetStateAction<ExtraFeeState[]>>;
}

interface FinancialPlanProviderInterface {
  children: React.ReactNode;
}

const FinancialPlanContext = createContext<FinancialPlanContenxtInterface>({} as FinancialPlanContenxtInterface);

const FinancialPlanProvider = ({ ...props }: FinancialPlanProviderInterface) => {
  // A. States
  const [isAdjustFPVisible, setAdjustFPVisible] = useState<boolean>(false);

  const { constant, hdb_input, others_input } = DUMMY_RESPONSE;

  const input = constant.version_by === "hdb" ? hdb_input : others_input;

  const [indexOfCurrentCpfDetail, setIndexOfCurrentCpfDetail] = useState<number>(0);

  const [cpfDetails, setCpfDetails] = useState<CpfDetailState[]>(
    combineObjArrays<CpfDetailConstant, CpfDetailInput>(constant.cpf_details, input.cpf_details, "uinfin")!
  );

  const [loanDetail, setLoanDetail] = useState<LoanDetailState>(combineObj<LoanDetailConstant, LoanDetailInput>(constant.loan_detail, input.loan_detail));

  const [stampFee, setStampFee] = useState<FeeState | undefined>(
    constant.stamp_fee && input.stamp_fee
      ? combineObjWithNewFields<FeeConstant, FeeInput, WithAdditionalAmount>(constant.stamp_fee, input.stamp_fee, { additional_amount: 0 })
      : undefined
  );

  const [conveyancingFee, setConveyancingFee] = useState<FeeState | undefined>(
    constant.conveyancing_fee && input.conveyancing_fee
      ? combineObjWithNewFields<FeeConstant, FeeInput, WithAdditionalAmount>(constant.conveyancing_fee, input.conveyancing_fee, { additional_amount: 0 })
      : undefined
  );

  const [registrationFee, setRegistrationFee] = useState<FeeState | undefined>(
    constant.registration_fee && input.registration_fee
      ? combineObjWithNewFields<FeeConstant, FeeInput, WithAdditionalAmount>(constant.registration_fee, input.registration_fee, { additional_amount: 0 })
      : undefined
  );

  const [hpsFees, setHpsFees] = useState<HpsFeeState[]>(
    constant.hps_fees && input.hps_fees
      ? combineObjArraysWithNewFields<HpsFeeConstant, HpsFeeInput, WithAdditionalAmount>(constant.hps_fees, input.hps_fees, "uinfin", { additional_amount: 0 })
      : []
  );
  const [extraFees, setExtraFees] = useState<ExtraFeeState[]>(
    constant.extra_fees && input.extra_fees
      ? combineObjArraysWithNewFields<ExtraFeeConstant, ExtraFeeInput, WithAdditionalAmount>(constant.extra_fees, input.extra_fees, "name_of_fee", {
          additional_amount: 0,
        })
      : []
  );

  // B. Derived values
  const fees: FeeState[] = [stampFee, conveyancingFee, registrationFee, ...(hpsFees ?? []), ...(extraFees ?? [])].filter((fee) => fee) as FeeState[];
  const totalFee = fees.reduce((accumulator, currentValue) => accumulator + currentValue.basic_amount + currentValue.additional_amount, 0);
  const feesPayableByCpf = fees.filter((fee) => fee.paid_by !== "cash") as FeeState[];
  const totalFeePayableByCpf = feesPayableByCpf.reduce(
    (accumulator, currentValue) => accumulator + currentValue.basic_amount + currentValue.additional_amount,
    0
  );
  // const totalFeePayableByCash = totalFee - totalFeePayableByCpf;

  const payableByCpfExcludingFees = cpfDetails.reduce((accumulator, currentValue) => accumulator + currentValue.cpf_val, 0);
  const payableByCpfIncludingFees = payableByCpfExcludingFees + totalFeePayableByCpf;

  const payableByCash = constant.total_payable - payableByCpfIncludingFees - loanDetail.loan_val;

  const derivedValue: DerivedValue = {
    payableByCpfExcludingFees,
    payableByCpfIncludingFees,
    totalFee,
    payableByCash: Math.max(0, payableByCash),
  };

  // C. Functions
  const toggleAdjustFPVisibility = () => {
    setAdjustFPVisible((prev) => !prev);
  };

  function getNextCpfDetail() {
    if (cpfDetails.length === 1) return null;

    let nextIndex = indexOfCurrentCpfDetail + 1;

    if (nextIndex > cpfDetails.length - 1) {
      nextIndex = 0;
    }

    while (cpfDetails[nextIndex].cpf_val === 0) {
      if (nextIndex > cpfDetails.length - 1) {
        nextIndex = 0;
        continue;
      }

      if (nextIndex === indexOfCurrentCpfDetail) {
        return null;
      }

      nextIndex++;
    }

    return cpfDetails[nextIndex];
  }

  // D. Effects
  useEffect(() => {
    if (payableByCash < 0) {
      updateSingleState(setLoanDetail, "loan_val", loanDetail.loan_val - Math.abs(payableByCash));
    }
  }, [payableByCash, loanDetail.loan_val]);

  useEffect(() => {
    if (loanDetail.loan_val < 0) {
      const nextCpfDetail = getNextCpfDetail();

      if (!nextCpfDetail) return;

      updateArrayState(setCpfDetails, "cpf_val", nextCpfDetail.cpf_val - Math.abs(loanDetail.loan_val), "uinfin", nextCpfDetail.uinfin);
      updateSingleState(setLoanDetail, "loan_val", 0);
    }
  }, [loanDetail.loan_val]);

  return (
    <FinancialPlanContext.Provider
      value={{
        isAdjustFPVisible,
        constant,
        hdb_input,
        others_input,
        indexOfCurrentCpfDetail,
        cpfDetails,
        loanDetail,
        stampFee,
        conveyancingFee,
        registrationFee,
        hpsFees,
        extraFees,
        derivedValue,
        toggleAdjustFPVisibility,
        setIndexOfCurrentCpfDetail,
        setCpfDetails,
        setLoanDetail,
        setStampFee,
        setConveyancingFee,
        setRegistrationFee,
        setHpsFees,
        setExtraFees,
      }}
    >
      {props.children}
    </FinancialPlanContext.Provider>
  );
};

const useFinancialPlanContext = () => {
  return useContext(FinancialPlanContext);
};

function combineObjArrays<T, U>(arr1: T[], arr2: U[], key: keyof T & keyof U): (T & U)[] {
  return arr1.map((arr1Item) => {
    const correspondingArr2Item = arr2.find((arr2Item) => (arr2Item[key] as string) === (arr1Item[key] as string));
    return {
      ...arr1Item,
      ...correspondingArr2Item!,
    };
  });
}

function combineObjArraysWithNewFields<T, U, V>(arr1: T[], arr2: U[], key: keyof T & keyof U, newFields: V) {
  return arr1.map((arr1Item) => {
    const correspondingArr2Item = arr2.find((arr2Item) => (arr2Item[key] as string) === (arr1Item[key] as string));
    return {
      ...arr1Item,
      ...correspondingArr2Item!,
      ...newFields,
    };
  });
}

function combineObj<T, U>(obj1: T, obj2: U): T & U {
  return {
    ...obj1,
    ...obj2,
  };
}

function combineObjWithNewFields<T, U, V>(obj1: T, obj2: U, newFields: V): T & U & V {
  return {
    ...obj1,
    ...obj2,
    ...newFields,
  };
}

export { FinancialPlanProvider, useFinancialPlanContext };
